/** Full-page event definition form, shared by registration, approval, and editing. */
import {
  Banner,
  Button,
  Card,
  Input,
  Select,
  TextArea,
} from '@servicecompressionllc/sc-design-system';
import { Check } from 'lucide-react';
import { EventFieldList } from './EventFieldList.js';
import { EventHandoff } from './EventHandoff.js';

import {
  useEventDefinitionLogic,
  ageOf,
  type EventDefinitionFormProps,
} from './useEventDefinitionLogic.js';

export const EventDefinitionForm = (props: EventDefinitionFormProps) => {
  const {
    entry,
    scopeOptions,
    isBusy,
    error,
    emitKeys,
    registered,
    onDone,
    onBuildWorkflow,
    onSendTest,
  } = props;
  const {
    removalWarning,
    confirmRemoval,
    setConfirmRemoval,
    slug,
    setSlug,
    description,
    setDescription,
    scopes,
    setScopes,
    fields,
    editingField,
    setEditingField,
    detectFields,
    editField,
    dropField,
    addField,
    newField,
    setNewField,
    addingField,
    setAddingField,
    signalOnly,
    setSignalOnly,
    example,
    setExample,
    heldCount,
    oldestHeld,
    processHeld,
    setProcessHeld,
    footerNote,
    isEditing,
    isNew,
    shape,
    charsOk,
    clash,
    slugValid,
    parsedExample,
    hasObserved,
    somethingToDeclare,
    choicesValid,
    submit,
  } = useEventDefinitionLogic(props);
  return (
    <form
      aria-label="Event definition"
      onSubmit={(e) => {
        e.preventDefault();
        submit();
      }}
      className="max-w-5xl"
    >
      <header className="mb-6">
        <h1 className="text-2xl font-bold text-ink flex items-center gap-2.5">
          {registered && (
            <span className="border-success-border bg-success-soft flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full border">
              <Check className="text-success h-3 w-3" strokeWidth={3.5} />
            </span>
          )}
          {registered ? 'Registered' : isEditing ? 'Edit event' : 'Register event'}
        </h1>
        <p className="text-ink-muted mt-1 text-sm">
          {registered
            ? `${registered.type} is in the catalog.`
            : isEditing
              ? 'The type string is fixed, because workflows reference it. Everything else is yours to change.'
              : entry
                ? `Held since ${new Date(entry.firstSeenAt ?? Date.now()).toLocaleString()} · ${String(entry.receivedCount)} received`
                : 'Nothing has sent this yet, so you’re describing it up front.'}
        </p>
      </header>

      <Card padding="md" className="flex flex-col gap-6">
        {registered ? (
          <EventHandoff
            entry={registered}
            emitKeys={emitKeys}
            isBusy={isBusy}
            onSendTest={() => onSendTest(registered)}
          />
        ) : (
          <>
            <div>
              <label className="text-ink text-sm font-semibold" htmlFor="event-slug">
                Event type
              </label>
              <p className="text-ink-subtle mt-0.5 text-xs">
                Lowercase and underscores. Senders put this in the request body, and it can’t change
                once workflows use it.
              </p>
              <div className="mt-2 flex items-center gap-2">
                <span className="text-ink-subtle flex-shrink-0 font-mono text-sm">custom.</span>
                <Input
                  id="event-slug"
                  className="font-mono"
                  error={slug.length > 0 && (!charsOk || Boolean(clash))}
                  value={slug}
                  disabled={!isNew}
                  onChange={(e) => setSlug(e.target.value.toLowerCase())}
                  placeholder="compressor_trip"
                />
              </div>
              {slug.length > 0 && !charsOk && (
                <p className="text-danger mt-1.5 text-xs">
                  Lowercase letters, numbers and underscores only.
                </p>
              )}
              {charsOk && clash && (
                <p className="text-danger mt-1.5 text-xs">
                  {clash.status === 'held'
                    ? 'Something is already sending this. Approve it from Needs review instead.'
                    : clash.status === 'blocked'
                      ? 'This type is blocked. Re-register it from the Catalog tab.'
                      : 'Already in the catalog.'}
                </p>
              )}
              {charsOk && !clash && isNew && (
                <p className="text-success mt-1.5 text-xs">
                  Available. Nothing in the catalog uses this name.
                </p>
              )}
            </div>

            <div>
              <label className="text-ink text-sm font-semibold" htmlFor="event-description">
                What it means
              </label>
              <p className="text-ink-subtle mt-0.5 text-xs">
                Shown wherever someone picks this event: in the workflow builder, and to the
                assistant.
              </p>
              <TextArea
                id="event-description"
                className="mt-2 min-h-[62px]"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="A compressor shut down on a protection trip…"
              />
            </div>

            <div>
              <span className="text-ink text-sm font-semibold">Who can build on it</span>
              <p className="text-ink-subtle mt-0.5 text-xs">
                Someone needs every scope listed here to use this event as a trigger. Leave it empty
                and any workflow author can.
              </p>
              <Select
                aria-label="Scopes"
                multiple
                className="mt-2"
                options={scopeOptions}
                value={scopes}
                onChange={setScopes}
              />
            </div>

            <div>
              <div className="flex items-center justify-between gap-3">
                <div>
                  <span className="text-ink text-sm font-semibold">Payload</span>
                  <p className="text-ink-subtle mt-0.5 text-xs">
                    Choose which event fields workflows can use.
                  </p>
                </div>
                {!signalOnly && !addingField && (
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={() => setAddingField(true)}
                  >
                    Add field
                  </Button>
                )}
              </div>
              {!signalOnly && addingField && (
                <div className="mt-4">
                  <>
                    <label htmlFor="new-event-field" className="text-sm font-semibold text-ink">
                      Payload field name
                    </label>
                    <div className="mt-2 flex items-start gap-2">
                      <Input
                        id="new-event-field"
                        autoFocus
                        className="min-w-0 flex-1"
                        value={newField}
                        maxLength={80}
                        placeholder="Field name from the payload, e.g. region"
                        onChange={(e) => setNewField(e.target.value)}
                      />
                      <Button
                        type="button"
                        variant="secondary"
                        className="shrink-0 whitespace-nowrap"
                        disabled={
                          !newField.trim() ||
                          fields.some((f) => f.name === newField.trim()) ||
                          fields.length >= 100
                        }
                        onClick={() => {
                          addField(newField.trim(), 'string');
                          setNewField('');
                          setAddingField(false);
                        }}
                      >
                        Add field
                      </Button>
                      <Button
                        type="button"
                        variant="secondary"
                        onClick={() => {
                          setAddingField(false);
                          setNewField('');
                        }}
                      >
                        Cancel
                      </Button>
                    </div>
                  </>
                </div>
              )}
              {signalOnly ? null : fields.length > 0 || hasObserved ? (
                <>
                  {fields.length > 0 && (
                    <EventFieldList
                      fields={fields}
                      editingField={editingField}
                      onEdit={setEditingField}
                      onChange={editField}
                      onRemove={dropField}
                    />
                  )}

                  {/* Observation never stops seeing the whole payload, so a
                      field the sender added — or one removed here earlier — is
                      offered back rather than leaving someone to wonder why a
                      variable is always empty. */}
                  {(shape?.fields ?? []).filter((o) => !fields.some((f) => f.name === o.name))
                    .length > 0 && (
                    <div className="border-info-border bg-info-soft mt-2 rounded-lg border p-3">
                      <p className="text-ink text-xs font-semibold">Also arriving, not declared</p>
                      <p className="text-ink-subtle mt-0.5 text-xs">
                        These are dropped before your steps run. Add one to let workflows use it.
                      </p>
                      <div className="mt-1.5 flex flex-wrap gap-1.5">
                        {(shape?.fields ?? [])
                          .filter((o) => !fields.some((f) => f.name === o.name))
                          .map((o) => (
                            <button
                              key={o.name}
                              type="button"
                              onClick={() => addField(o.name, o.type)}
                              className="border-outline-strong bg-surface text-ink hover:bg-surface-hover rounded border px-2 py-0.5 font-mono text-xs"
                            >
                              + {o.name}
                            </button>
                          ))}
                      </div>
                    </div>
                  )}

                  {fields.length === 0 && (
                    <p className="text-danger mt-2 text-xs">
                      No fields left. Keep at least one, or a workflow has nothing to listen for.
                    </p>
                  )}
                </>
              ) : (
                <>
                  <p className="text-ink-subtle mt-0.5 text-xs">
                    {isEditing
                      ? 'Nothing has sent this yet. Paste one payload the sender will produce, or leave it and the fields appear here once the first event arrives.'
                      : 'Paste one payload the sender will produce. Only the fields you declare here ever reach a workflow.'}
                  </p>

                  {/* Offered on an EDIT too. Registering signal-only and coming
                      back to fill the payload in is an ordinary thing to do,
                      and the field editor cannot serve it: that needs an
                      observed shape to edit, which by definition is not there
                      yet. */}
                  {!signalOnly && (
                    <TextArea
                      aria-label="Example payload"
                      className="mt-2 min-h-[96px] font-mono text-xs"
                      error={!parsedExample.ok}
                      value={example}
                      onChange={(e) => setExample(e.target.value)}
                      placeholder={'{\n  "unit_id": "2078",\n  "trip_code": "HI-DISCH-TEMP"\n}'}
                    />
                  )}
                  <Button
                    type="button"
                    variant="secondary"
                    className="mt-2"
                    onClick={detectFields}
                    disabled={
                      !parsedExample.ok ||
                      !parsedExample.value ||
                      Object.keys(parsedExample.value).length === 0
                    }
                  >
                    Detect fields
                  </Button>
                  {!parsedExample.ok && (
                    <p className="text-danger mt-1.5 text-xs">{parsedExample.message}</p>
                  )}
                </>
              )}

              {entry?.lastPayload && !signalOnly && (
                <details className="mt-4">
                  <summary className="cursor-pointer text-sm text-ink-muted">
                    Latest received payload
                  </summary>
                  <pre className="border-outline bg-surface mt-2 max-h-80 overflow-auto whitespace-pre-wrap rounded-lg border p-4 text-xs">
                    {JSON.stringify(entry.lastPayload, null, 2)}
                  </pre>
                </details>
              )}

              {fields.length === 0 && (
                <label className="text-ink-muted mt-2.5 flex cursor-pointer items-center gap-2 text-xs">
                  <input
                    type="checkbox"
                    className="accent-primary h-3.5 w-3.5"
                    checked={signalOnly}
                    onChange={(e) => setSignalOnly(e.target.checked)}
                  />
                  This event has no payload
                </label>
              )}
            </div>

            {heldCount > 0 && (
              <label
                className={`flex cursor-pointer items-start gap-3 rounded-lg border p-3 ${
                  processHeld ? 'border-info-border bg-info-soft' : 'border-outline bg-surface'
                }`}
              >
                <input
                  type="checkbox"
                  aria-label="Process the events already received"
                  className="accent-primary mt-0.5 h-4 w-4"
                  checked={processHeld}
                  onChange={(e) => setProcessHeld(e.target.checked)}
                />
                <span className="min-w-0">
                  <span className="text-ink block text-xs font-semibold">
                    Process the {heldCount} event{heldCount === 1 ? '' : 's'} already received
                  </span>
                  <span className="text-ink-subtle mt-0.5 block text-xs">
                    {oldestHeld
                      ? `Oldest arrived ${ageOf(oldestHeld)} ago. They run through your workflows as if they had just come in.`
                      : 'They run through your workflows as if they had just come in.'}
                  </span>
                </span>
              </label>
            )}
          </>
        )}

        {!signalOnly && !choicesValid && (
          <Banner variant="warning">
            <Banner.Body>
              Enter 1–50 unique, non-empty choices of up to 200 characters each.
            </Banner.Body>
          </Banner>
        )}
        {error &&
          (!('code' in error) || error.code !== 'event_fields_in_use' || removalWarning) && (
            <Banner variant={removalWarning ? 'warning' : 'danger'}>
              <Banner.Body>
                {error.message}
                {removalWarning && (
                  <label className="mt-3 flex items-center gap-2">
                    <input
                      type="checkbox"
                      className="accent-primary"
                      checked={confirmRemoval}
                      onChange={(e) => setConfirmRemoval(e.target.checked)}
                    />
                    Remove these fields even though workflows use them
                  </label>
                )}
              </Banner.Body>
            </Banner>
          )}
      </Card>

      {registered ? (
        <div className="mt-6 flex flex-wrap items-center gap-3">
          <Button type="button" variant="secondary" onClick={onDone}>
            Done
          </Button>
          <Button type="button" onClick={() => onBuildWorkflow(registered)}>
            Build a workflow on it
          </Button>
        </div>
      ) : (
        <div className="mt-6 flex flex-wrap items-center gap-3">
          {/* Only what the reader cannot infer from the button. "Routing
              starts now" restated the act of registering; what happens to a
              held backlog does not. */}
          {footerNote && (
            <p className="text-ink-subtle mr-auto min-w-0 max-w-[30ch] text-xs">{footerNote}</p>
          )}
          <Button type="button" variant="secondary" onClick={onDone}>
            Cancel
          </Button>
          <Button
            type="button"
            className="whitespace-nowrap"
            onClick={submit}
            disabled={
              (Boolean(removalWarning) && !confirmRemoval) ||
              !slugValid ||
              !parsedExample.ok ||
              !somethingToDeclare ||
              (!signalOnly && !choicesValid) ||
              isBusy
            }
          >
            {isBusy ? 'Saving…' : isEditing ? 'Save changes' : 'Register event'}
          </Button>
        </div>
      )}
    </form>
  );
};
